/**
 * Tracking Store - Buyer PWA
 * Manages GPS tracking state for live rider location updates
 */

import { create } from 'zustand';
import { logger } from '@/lib/logger';
import * as api from '@/lib/api';

export interface GPSPosition {
  order_id: string;
  rider_id: string;
  lat: number;
  lng: number;
  speed_kph: number | null;
  heading_deg: number | null;
  updated_at: string;
  eta_text: string | null;
  distance_meters: number | null;
}

export interface GPSHistoryPoint {
  lat: number;
  lng: number;
  timestamp: string;
  speedKmh: number | null;
  heading: number | null;
  accuracy: number | null;
}

interface TrackingState {
  // Current position
  currentPosition: GPSPosition | null;
  isLoading: boolean;
  error: string | null;
  
  // History trail
  historyPoints: GPSHistoryPoint[];
  isLoadingHistory: boolean;
  
  // Offline state
  isOnline: boolean;
  lastUpdatedAt: string | null;
  
  // Actions
  fetchPosition: (orderId: string) => Promise<void>;
  fetchHistory: (orderId: string, minutes?: number) => Promise<void>;
  updatePosition: (position: GPSPosition) => void;
  setOnlineStatus: (isOnline: boolean) => void;
  clearTracking: () => void;
}

export const useTrackingStore = create<TrackingState>((set) => ({
  currentPosition: null,
  isLoading: false,
  error: null,
  historyPoints: [],
  isLoadingHistory: false,
  isOnline: true,
  lastUpdatedAt: null,

  fetchPosition: async (orderId: string) => {
    set({ isLoading: true, error: null });
    try {
      const position = await api.getOrderGPS(orderId);
      set({
        currentPosition: position,
        isLoading: false,
        lastUpdatedAt: new Date().toISOString(),
      });
      logger.info('GPS position fetched', { orderId });
    } catch (error: any) {
      logger.error('Failed to fetch GPS position', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to fetch rider location',
      });
    }
  },

  fetchHistory: async (orderId: string, minutes = 30) => {
    set({ isLoadingHistory: true });
    try {
      const response = await api.getOrderGPSHistory(orderId, minutes);
      set({
        historyPoints: response.history || [],
        isLoadingHistory: false,
      });
      logger.info('GPS history fetched', { orderId, count: response.count });
    } catch (error: any) {
      // Silently fail - GPS history is optional
      logger.warn('GPS history not available', { 
        orderId, 
        error: error.response?.data?.message || error.message 
      });
      set({ isLoadingHistory: false, historyPoints: [] });
    }
  },

  updatePosition: (position: GPSPosition) => {
    set({
      currentPosition: position,
      lastUpdatedAt: new Date().toISOString(),
      error: null,
    });
    logger.info('GPS position updated via WebSocket', { orderId: position.order_id });
  },

  setOnlineStatus: (isOnline: boolean) => {
    set({ isOnline });
    if (isOnline) {
      logger.info('Tracking: Back online');
    } else {
      logger.warn('Tracking: Offline');
    }
  },

  clearTracking: () => {
    set({
      currentPosition: null,
      historyPoints: [],
      error: null,
      lastUpdatedAt: null,
    });
  },
}));

